import { useSyncExternalStore } from "react";

import { getEntityByToken } from "../../../data/get_entity";

import type { FavoriteCollection } from "./FavoriteCollectionPicker";

/**
 * Shared favorites store for the "Saved Products & Cart" prototype.
 *
 * The home feed and the cart live on separate routes that mount independently,
 * so the saved-membership state cannot live in a React provider inside either
 * page. Instead it lives here at module scope and is read via
 * useSyncExternalStore, so favoriting a product on the feed persists to the
 * cart route (and back) without touching the shared App shell.
 */

export const ALL_FAVORITES_COLLECTION_ID = "all-favorites";

export type ProductTokensByCollection = Record<string, string[]>;

export const getProductImageUrl = (productToken: string): string | undefined =>
  getEntityByToken(productToken, "product")?.imageUrl;

export const INITIAL_ALL_FAVORITES_IMAGE_URL = getProductImageUrl(
  "atc_refined_moma_9"
);

export const INITIAL_COLLECTIONS: FavoriteCollection[] = [
  {
    id: ALL_FAVORITES_COLLECTION_ID,
    name: "All saved products",
    imageUrl: INITIAL_ALL_FAVORITES_IMAGE_URL,
  },
  {
    id: "kitchen-fall-2026",
    name: "Kitchen, Fall 2026",
    imageUrl: getProductImageUrl("catalog_prod_prod-19"),
  },
  {
    id: "japanese-market",
    name: "Japanese market",
    imageUrl: getProductImageUrl("p_kubhyw672d"),
  },
  {
    id: "fall-chocolates",
    name: "Fall chocolates",
    imageUrl: getProductImageUrl("p_z67nypuv9s"),
  },
  {
    id: "holiday-cards",
    name: "Holiday cards",
    imageUrl: getProductImageUrl("p_hm44x2pgpm"),
  },
  {
    id: "fall-scents",
    name: "Fall scents",
    imageUrl: getProductImageUrl("p_c6457xt61l"),
  },
  {
    id: "valentines-day",
    name: "Valentine's day",
    imageUrl: getProductImageUrl("nopalera-2"),
  },
  {
    id: "pantry-restock",
    name: "Pantry restock",
    imageUrl: getProductImageUrl("ramen-1"),
  },
  {
    id: "spring-tabletop",
    name: "Spring tabletop",
    imageUrl: getProductImageUrl("plant-dyed-1"),
  },
  {
    id: "gifts-under-25",
    name: "Gifts under $25",
    imageUrl: getProductImageUrl("nyc-snack-1"),
  },
  {
    id: "korean-pantry",
    name: "Korean pantry",
    imageUrl: getProductImageUrl("potluck-kimchi-base-1"),
  },
];

// --- Module-level store ------------------------------------------------------

type Updater<T> = T | ((previous: T) => T);

const resolveUpdater = <T,>(updater: Updater<T>, previous: T): T =>
  typeof updater === "function"
    ? (updater as (previous: T) => T)(previous)
    : updater;

const STORAGE_KEY = "cc-saved-products-cart";

interface PersistedState {
  collections: FavoriteCollection[];
  productTokensByCollection: ProductTokensByCollection;
}

const loadPersistedState = (): PersistedState | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw) as PersistedState;
    if (!parsed.collections || !parsed.productTokensByCollection) {
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
};

const persistedState = loadPersistedState();

// Seeded collections added after a browser first persisted state would
// otherwise never appear there, so append any the persisted list is missing.
const withMissingSeededCollections = (
  collections: FavoriteCollection[]
): FavoriteCollection[] => {
  const knownIds = new Set(collections.map((collection) => collection.id));
  return [
    ...collections,
    ...INITIAL_COLLECTIONS.filter((collection) => !knownIds.has(collection.id)),
  ];
};

let collectionsState: FavoriteCollection[] = persistedState
  ? withMissingSeededCollections(persistedState.collections)
  : INITIAL_COLLECTIONS;
let productTokensByCollectionState: ProductTokensByCollection =
  persistedState?.productTokensByCollection ?? {};

const listeners = new Set<() => void>();

const persist = () => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        collections: collectionsState,
        productTokensByCollection: productTokensByCollectionState,
      })
    );
  } catch {
    // Ignore quota / serialization errors — persistence is best-effort.
  }
};

const emit = () => {
  persist();
  for (const listener of listeners) {
    listener();
  }
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const setCollections = (updater: Updater<FavoriteCollection[]>): void => {
  collectionsState = resolveUpdater(updater, collectionsState);
  emit();
};

export const setProductTokensByCollection = (
  updater: Updater<ProductTokensByCollection>
): void => {
  productTokensByCollectionState = resolveUpdater(
    updater,
    productTokensByCollectionState
  );
  emit();
};

export const useCollections = (): FavoriteCollection[] =>
  useSyncExternalStore(subscribe, () => collectionsState);

export const useProductTokensByCollection = (): ProductTokensByCollection =>
  useSyncExternalStore(subscribe, () => productTokensByCollectionState);

// --- Pure membership helpers (shared by the feed logic and the cart) ---------

export const getLatestCollectionImageUrl = (
  collectionId: string,
  productTokensByCollection: ProductTokensByCollection
): string | undefined => {
  const collectionProductTokens = productTokensByCollection[collectionId] ?? [];
  const latestProductToken =
    collectionProductTokens[collectionProductTokens.length - 1];
  return latestProductToken ? getProductImageUrl(latestProductToken) : undefined;
};

export const getFirstCollectionImageUrl = (
  collectionId: string,
  productTokensByCollection: ProductTokensByCollection
): string | undefined => {
  const collectionProductTokens = productTokensByCollection[collectionId] ?? [];
  const firstProductToken = collectionProductTokens[0];
  return firstProductToken ? getProductImageUrl(firstProductToken) : undefined;
};

export const addProductToCollection = (
  productTokensByCollection: ProductTokensByCollection,
  collectionId: string,
  productToken: string
): ProductTokensByCollection => ({
  ...productTokensByCollection,
  [collectionId]: [
    ...(productTokensByCollection[collectionId] ?? []).filter(
      (collectionProductToken) => collectionProductToken !== productToken
    ),
    productToken,
  ],
});

export const removeProductFromCollection = (
  productTokensByCollection: ProductTokensByCollection,
  collectionId: string,
  productToken: string
): ProductTokensByCollection => ({
  ...productTokensByCollection,
  [collectionId]: (productTokensByCollection[collectionId] ?? []).filter(
    (collectionProductToken) => collectionProductToken !== productToken
  ),
});

export const updateAllFavoritesCollectionImage = (
  collections: FavoriteCollection[],
  imageUrl: string | undefined
): FavoriteCollection[] =>
  collections.map((collection) =>
    collection.id === ALL_FAVORITES_COLLECTION_ID
      ? { ...collection, imageUrl }
      : collection
  );
